// frontend/src/pages/freelancer/FreelancerBookings.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import "./freelancerStyles.css";
// Walk-in (Phase 1): shared launcher, same component the dashboards use.
import WalkInButton from "../../components/WalkInButton";
import {
  formatDisplayDate,
  getTodayString,
  parseToYYYYMMDD,
} from "../../utils/dateHelpers";
import AddLookModal from "../../components/AddLookModal";

// P0a (F3 — service selection in bundles/groups): provider self-booking is paused
// until the pricing-engine fix lands. Set to `true` (phase P10) to restore the
// "+ Add New Booking" and empty-state "Create First Booking" buttons.
const PROVIDER_SELF_BOOKING_ENABLED = false;

// Test booking badge component
const TestBadge = ({ isTest }) => {
  if (!isTest) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        marginLeft: "8px",
        fontSize: "10px",
        fontWeight: "600",
        borderRadius: "4px",
        backgroundColor: "#f59e0b",
        color: "white",
        verticalAlign: "middle",
      }}
    >
      TEST
    </span>
  );
};

function FreelancerBookings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("today");
  const [errorMsg, setErrorMsg] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addBookingData, setAddBookingData] = useState({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    serviceId: "",
    serviceName: "",
    bookingDate: "",
    bookingTime: "",
    duration: "",
    price: "",
    notes: "",
  });
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Add Look states
  const [looksEligibleIds, setLooksEligibleIds] = useState(null); // null = not loaded yet
  // Map of bookingId -> existingLook { lookId, status, imageCount } for add-to-existing mode
  const [looksExistingMap, setLooksExistingMap] = useState({});
  const [eligibleBookings, setEligibleBookings] = useState([]);
  const [addLookBooking, setAddLookBooking] = useState(null);

  const bookingsBasePath = location.pathname.startsWith(
    "/dashboard/booth-renter",
  )
    ? "/dashboard/booth-renter/bookings"
    : "/dashboard/freelancer/bookings";
  const dashboardBasePath = location.pathname.startsWith(
    "/dashboard/booth-renter",
  )
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error.");
      return null;
    }
    return baseURL;
  }, []);

  useEffect(() => {
    // Fetch services for dropdown
    const fetchServices = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;
        const res = await axios.get(`${baseURL}/api/staff/services`, {
          headers,
        });
        setServices(res.data.services || []);
      } catch (err) {
      }
    };
    fetchServices();

    const fetchBookings = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;
        const res = await axios.get(`${baseURL}/api/freelancer/bookings`, {
          headers,
        });
        setBookings(res.data.bookings || []);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        } else {
          setErrorMsg("Failed to load bookings");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [navigate, getAuthHeaders, getApiBaseUrl]);

  // Load eligible booking IDs for "Add Look" buttons (non-blocking)
  const loadEligibleBookings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return [];
      const res = await axios.get(`${baseURL}/api/looks/eligible-bookings`, {
        headers,
      });
      const eligible = res.data.eligibleBookings || [];
      const ids = new Set(eligible.map((b) => b._id));
      setLooksEligibleIds(ids);
      const existingMap = {};
      eligible.forEach((b) => {
        if (b.existingLook) existingMap[b._id] = b.existingLook;
      });
      setLooksExistingMap(existingMap);
      setEligibleBookings(eligible);
      return eligible;
    } catch {
      setLooksEligibleIds(new Set());
      setEligibleBookings([]);
      return [];
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    loadEligibleBookings();
  }, [loadEligibleBookings]);

  // Keep adding Looks one after another: after a save, open the next completed
  // booking that still has no Look.
  const handleAddAnother = useCallback(async () => {
    const eligible = await loadEligibleBookings();
    const next = eligible.find((b) => !b.existingLook) || null;
    if (!next) {
      setAddLookBooking(null);
      return;
    }
    setAddLookBooking(next);
  }, [loadEligibleBookings]);

  const getStatusColor = (status) => {
    const colors = {
      confirmed: { bg: "#d1fae5", border: "#10b981", text: "#065f46" },
      pending: { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
      completed: { bg: "#dbeafe", border: "#3b82f6", text: "#1e40af" },
      cancelled: { bg: "#fee2e2", border: "#ef4444", text: "#991b1b" },
      canceled: { bg: "#fee2e2", border: "#ef4444", text: "#991b1b" },
      "no-show": { bg: "#f3f4f6", border: "#9ca3af", text: "#374151" },
    };
    return colors[status?.toLowerCase()] || colors.pending;
  };

  const formatDateHeading = (dateString) => {
    if (!dateString) return "Unknown date";
    const date = new Date(`${dateString}T12:00:00`);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getBookingDateString = (booking) =>
    parseToYYYYMMDD(booking.appointmentDate || booking.bookingDate);

  const getBookingStartDateTime = (booking) => {
    const dateString = getBookingDateString(booking);
    const startTime = booking.startTime || booking.bookingTime;

    if (!dateString || !startTime) return null;

    const startDateTime = new Date(`${dateString}T${startTime}`);
    return Number.isNaN(startDateTime.getTime()) ? null : startDateTime;
  };

  const getDisplayAmount = (booking) => {
    if (!booking) return 0;

    const canonicalCancellationPayout =
      Number(booking.cancellationPayoutAmount) || 0;
    if (canonicalCancellationPayout > 0) {
      return canonicalCancellationPayout;
    }

    const isCanceled = ["cancelled", "canceled"].includes(
      booking.status?.toLowerCase(),
    );

    if (isCanceled && booking.cancellationFeeDistribution) {
      // The professional's shares ADDED, matching utils/paymentSplitCalculator.js.
      // ownerShare is deliberately excluded - it is the BUSINESS's share.
      const payoutAmount =
        (Number(booking.cancellationFeeDistribution.freelancerShare) || 0) +
        (Number(booking.cancellationFeeDistribution.professionalShare) || 0);

      if (payoutAmount > 0) {
        return payoutAmount;
      }
    }

    return (
      booking.servicePriceAtBooking || booking.totalPrice || booking.price || 0
    );
  };

  const filteredBookings = bookings.filter((booking) => {
    const bookingDate = getBookingDateString(booking);
    const today = getTodayString();

    if (filter === "today") {
      // All of today's bookings, regardless of status.
      return bookingDate === today;
    }
    if (filter === "all") return true;
    if (filter === "upcoming") {
      const startDateTime = getBookingStartDateTime(booking);
      const now = new Date();
      return (
        ["confirmed", "pending"].includes(booking.status?.toLowerCase()) &&
        startDateTime &&
        startDateTime > now
      );
    }
    if (filter === "past") {
      return (
        booking.status?.toLowerCase() === "completed" ||
        (bookingDate &&
          bookingDate < today &&
          booking.status?.toLowerCase() !== "cancelled")
      );
    }
    if (filter === "cancelled") {
      return booking.status?.toLowerCase() === "cancelled";
    }
    return true;
  });

  const groupedBookings = filteredBookings.reduce((groups, booking) => {
    const bookingDate = getBookingDateString(booking) || "unknown";
    if (!groups[bookingDate]) {
      groups[bookingDate] = [];
    }
    groups[bookingDate].push(booking);
    return groups;
  }, {});

  const filterOptions = [
    { value: "today", label: "Today", icon: "📅" },
    { value: "upcoming", label: "Upcoming", icon: "🗓️" },
    { value: "past", label: "Past", icon: "🕘" },
    { value: "cancelled", label: "Cancelled", icon: "❌" },
    { value: "all", label: "All", icon: "📋" },
  ];

  const getFilterCount = (targetFilter) => {
    return bookings.filter((booking) => {
      const bookingDate = getBookingDateString(booking);
      const today = getTodayString();

      if (targetFilter === "today") {
        // All of today's bookings, regardless of status.
        return bookingDate === today;
      }

      if (targetFilter === "all") return true;

      if (targetFilter === "upcoming") {
        const startDateTime = getBookingStartDateTime(booking);
        const now = new Date();
        return (
          ["confirmed", "pending"].includes(booking.status?.toLowerCase()) &&
          startDateTime &&
          startDateTime > now
        );
      }

      if (targetFilter === "past") {
        return (
          booking.status?.toLowerCase() === "completed" ||
          (bookingDate &&
            bookingDate < today &&
            booking.status?.toLowerCase() !== "cancelled")
        );
      }

      if (targetFilter === "cancelled") {
        return ["cancelled", "canceled"].includes(
          booking.status?.toLowerCase(),
        );
      }

      return true;
    }).length;
  };

  const handleAddBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.post(
        `${baseURL}/api/freelancer/bookings/manual`,
        {
          ...addBookingData,
          status: "confirmed",
        },
        { headers },
      );

      // Add new booking to list
      setBookings([res.data.booking, ...bookings]);

      // Reset form and close modal
      setAddBookingData({
        clientName: "",
        clientEmail: "",
        clientPhone: "",
        serviceId: "",
        serviceName: "",
        bookingDate: "",
        bookingTime: "",
        duration: "",
        price: "",
        notes: "",
      });
      setShowAddModal(false);
      alert("Booking created successfully!");
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(err?.response?.data?.message || "Failed to create booking");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleServiceChange = (e) => {
    const serviceId = e.target.value;
    const selectedService = services.find((s) => s._id === serviceId);
    if (selectedService) {
      setAddBookingData({
        ...addBookingData,
        serviceId: serviceId,
        serviceName: selectedService.name,
        price: selectedService.price || "",
        duration: parseInt(selectedService.duration) || 60,
      });
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div className="loading">Loading bookings...</div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          padding: isMobile ? "12px" : "24px",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            marginBottom: "24px",
            gap: isMobile ? "12px" : "16px",
          }}
        >
          <button
            onClick={() => navigate(dashboardBasePath)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 18px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              background: "#fff",
              color: "#333",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f5f5f5";
              e.currentTarget.style.borderColor = "#001d4a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#ddd";
            }}
          >
            <span>←</span> Back to Dashboard
          </button>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flex: 1,
              justifyContent: "flex-end",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              onClick={() => navigate(`${dashboardBasePath}/bookings/import`)}
              style={{
                padding: "10px 20px",
                background: "#fff",
                color: "#2d6da3",
                border: "1px solid #2d6da3",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              📥 Import Appointments
            </button>
            {/* Walk-in (Phase 1): sits OUTSIDE the PROVIDER_SELF_BOOKING_ENABLED gate
                on purpose - that flag pauses the provider self-booking form, not the
                desk recording a customer who is standing there. Serves BOTH R4 and
                R7: /dashboard/freelancer/bookings and /dashboard/booth-renter/bookings
                render this same component. */}
            <WalkInButton label="Walk-in" />
            {PROVIDER_SELF_BOOKING_ENABLED && (
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  padding: "10px 24px",
                  background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(45, 109, 163, 0.3)",
                  transition: "all 0.2s",
                  width: isMobile ? "100%" : "auto",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(45, 109, 163, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(45, 109, 163, 0.3)";
                }}
              >
                + Add New Booking
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #2d6da3 100%)",
            borderRadius: "16px",
            padding: isMobile ? "24px" : "32px",
            marginBottom: "24px",
            color: "white",
            boxShadow: "0 8px 24px rgba(45, 109, 163, 0.2)",
          }}
        >
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: isMobile ? "24px" : "32px",
              fontWeight: "700",
            }}
          >
            📅 My Bookings
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? "14px" : "16px",
              opacity: 0.9,
            }}
          >
            Manage your appointments and schedule
          </p>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: "16px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "8px",
              color: "#991b1b",
              marginBottom: "24px",
            }}
          >
            {errorMsg}
          </div>
        )}

        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            padding: isMobile ? "0" : "0 4px",
          }}
        >
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                background:
                  filter === option.value
                    ? "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)"
                    : "#fff",
                color: filter === option.value ? "#fff" : "#6b7280",
                border:
                  filter === option.value
                    ? "2px solid #2d6da3"
                    : "2px solid #e5e7eb",
                fontSize: "14px",
                fontWeight: filter === option.value ? "600" : "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow:
                  filter === option.value
                    ? "0 4px 12px rgba(45, 109, 163, 0.2)"
                    : "none",
              }}
              onMouseEnter={(e) => {
                if (filter !== option.value) {
                  e.currentTarget.style.borderColor = "#2d6da3";
                  e.currentTarget.style.background = "#f0f9ff";
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== option.value) {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.background = "#fff";
                }
              }}
            >
              <span style={{ marginRight: "6px" }}>{option.icon}</span>
              {option.label} ({getFilterCount(option.value)})
            </button>
          ))}
        </div>

        {Object.keys(groupedBookings).length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: isMobile ? "48px 20px" : "80px 20px",
              background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
              borderRadius: "16px",
              border: "2px dashed #d1d5db",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "64px" : "80px",
                marginBottom: "16px",
              }}
            >
              📅
            </div>
            <h3
              style={{
                color: "#374151",
                marginBottom: "12px",
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "600",
              }}
            >
              No bookings found
            </h3>
            <p
              style={{
                color: "#6b7280",
                fontSize: isMobile ? "15px" : "16px",
                marginBottom: "24px",
              }}
            >
              {filter === "today"
                ? "You have no more appointments for today"
                : filter === "upcoming"
                  ? "You have no upcoming appointments"
                  : "No bookings match your filters"}
            </p>
            {PROVIDER_SELF_BOOKING_ENABLED && (
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  padding: "12px 32px",
                  background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(45, 109, 163, 0.3)",
                }}
              >
                Create First Booking
              </button>
            )}
          </div>
        ) : (
          Object.keys(groupedBookings)
            .sort((a, b) => {
              if (a === "unknown") return 1;
              if (b === "unknown") return -1;
              return new Date(a) - new Date(b);
            })
            .map((date) => (
              <div key={date} style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                    paddingBottom: "12px",
                    borderBottom: "3px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      width: "4px",
                      height: "24px",
                      background:
                        "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      borderRadius: "2px",
                    }}
                  ></div>
                  <h3
                    style={{
                      fontSize: isMobile ? "18px" : "20px",
                      color: "#111",
                      margin: 0,
                      fontWeight: "700",
                    }}
                  >
                    {formatDateHeading(date)}
                  </h3>
                  <span
                    style={{
                      marginLeft: "auto",
                      padding: "4px 12px",
                      background: "#f0f9ff",
                      color: "#2d6da3",
                      borderRadius: "12px",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    {groupedBookings[date].length} booking
                    {groupedBookings[date].length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(380px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {groupedBookings[date]
                    .slice()
                    .sort((a, b) =>
                      (a.startTime || a.bookingTime || "").localeCompare(
                        b.startTime || b.bookingTime || "",
                      ),
                    )
                    .map((booking) => {
                      const statusColor = getStatusColor(booking.status);
                      return (
                        <div
                          key={booking._id || booking.id}
                          style={{
                            background: "#fff",
                            border: "2px solid #e5e7eb",
                            borderRadius: "12px",
                            padding: "20px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            navigate(
                              `${bookingsBasePath}/${booking._id || booking.id}`,
                            )
                          }
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#2d6da3";
                            e.currentTarget.style.boxShadow =
                              "0 8px 24px rgba(45, 109, 163, 0.15)";
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "16px",
                              gap: "12px",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: "17px",
                                    fontWeight: "700",
                                    margin: 0,
                                    color: "#111",
                                  }}
                                >
                                  {booking.serviceName || "Service"}
                                </h4>
                                <TestBadge isTest={booking.isTestBooking} />
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "6px 14px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "700",
                                background: statusColor.bg,
                                border: `2px solid ${statusColor.border}`,
                                color: statusColor.text,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {booking.status || "pending"}
                            </div>
                          </div>

                          <div
                            style={{
                              padding: "12px",
                              background: "#f9fafb",
                              borderRadius: "8px",
                              marginBottom: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "6px",
                              }}
                            >
                              <span style={{ fontSize: "16px" }}>👤</span>
                              <p
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  color: "#374151",
                                  margin: 0,
                                }}
                              >
                                {booking.customerName ||
                                  booking.clientName ||
                                  "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span style={{ fontSize: "16px" }}>🕐</span>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#6b7280",
                                  margin: 0,
                                  fontWeight: "500",
                                }}
                              >
                                {formatDisplayDate(
                                  booking.appointmentDate ||
                                    booking.bookingDate,
                                )}{" "}
                                at{" "}
                                {booking.startTime ||
                                  booking.bookingTime ||
                                  "TBD"}
                              </p>
                            </div>
                          </div>

                          {booking.duration && (
                            <div
                              style={{
                                padding: "8px 12px",
                                background: "#f0f9ff",
                                border: "1px solid #bfdbfe",
                                borderRadius: "6px",
                                marginBottom: "12px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#1e40af",
                                  margin: 0,
                                  fontWeight: "600",
                                }}
                              >
                                Duration: {booking.duration} minutes
                              </p>
                            </div>
                          )}

                          {booking.paidInExternalSystem && (
                            <div
                              style={{
                                padding: "8px 12px",
                                background: "#f3f4f6",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                marginBottom: "12px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  margin: 0,
                                  fontWeight: "600",
                                }}
                              >
                                🏛️ Historical Appointment
                              </p>
                            </div>
                          )}

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: "12px",
                              paddingTop: "12px",
                              borderTop: "1px solid #e5e7eb",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#111827",
                              }}
                            >
                              ${getDisplayAmount(booking).toFixed(2)}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `${bookingsBasePath}/${booking._id || booking.id}`,
                                );
                              }}
                              style={{
                                padding: "8px 12px",
                                fontSize: "13px",
                                background: "#f0f9ff",
                                color: "#1e40af",
                                border: "1px solid #bfdbfe",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "600",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#dbeafe";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f0f9ff";
                              }}
                            >
                              👁️ View
                            </button>

                            {/* Cancel walk-in — ONLY for a walk-in still awaiting
                                payment, never for an online pending booking (those
                                go through the normal cancellation flow). */}
                            {booking.isWalkIn &&
                              booking.status === "pending_payment" && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (
                                      !window.confirm(
                                        "Cancel this walk-in? The held time slot will be released immediately.",
                                      )
                                    ) {
                                      return;
                                    }
                                    try {
                                      const base =
                                        process.env.REACT_APP_API_BASE_URL;
                                      const token =
                                        localStorage.getItem("ogestyle_token");
                                      await axios.post(
                                        `${base}/api/bookings/walk-in/${booking._id}/cancel`,
                                        {},
                                        {
                                          headers: {
                                            Authorization: `Bearer ${token}`,
                                            "Content-Type": "application/json",
                                          },
                                        },
                                      );
                                      // The booking is deleted server-side, so drop
                                      // it from local state. `fetchBookings` is
                                      // scoped inside an effect, not the render.
                                      setBookings((prev) =>
                                        prev.filter(
                                          (b) =>
                                            String(b._id) !== String(booking._id),
                                        ),
                                      );
                                    } catch (err) {
                                      window.alert(
                                        err?.response?.data?.message ||
                                          "Unable to cancel this walk-in.",
                                      );
                                    }
                                  }}
                                  style={{
                                    padding: "8px 12px",
                                    fontSize: "13px",
                                    background: "#fee2e2",
                                    color: "#991b1b",
                                    border: "1px solid #fecaca",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#fecaca";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "#fee2e2";
                                  }}
                                >
                                  ✖ Cancel walk-in
                                </button>
                              )}
                            {booking.status === "completed" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddLookBooking(booking);
                                }}
                                style={{
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  background: "#f0fdf4",
                                  color: "#15803d",
                                  border: "1px solid #86efac",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#dcfce7";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#f0fdf4";
                                }}
                              >
                                {looksExistingMap[booking._id]?.imageCount >= 4
                                  ? "📸 Manage Look"
                                  : looksExistingMap[booking._id]
                                    ? "📸 Add Another Look"
                                    : "📸 Add Look"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
        )}
      </div>

      {/* Add Look Modal (shared component) */}
      {addLookBooking && (
        <AddLookModal
          key={addLookBooking._id}
          booking={addLookBooking}
          existingLook={
            addLookBooking ? looksExistingMap[addLookBooking._id] || null : null
          }
          getAuthHeaders={getAuthHeaders}
          getApiBaseUrl={getApiBaseUrl}
          onClose={() => setAddLookBooking(null)}
          onAddAnother={handleAddAnother}
          onSuccess={(bookingId) => {
            setLooksEligibleIds((prev) => {
              const next = new Set(prev);
              next.delete(bookingId);
              return next;
            });
          }}
        />
      )}

      {/* Add Booking Modal */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: isMobile ? "20px" : "30px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: isMobile ? "20px" : "24px" }}>
                Add Manual Booking
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            {errorMsg && (
              <div
                style={{
                  background: "#fee",
                  border: "1px solid #fcc",
                  borderRadius: "6px",
                  padding: "12px",
                  marginBottom: "15px",
                  color: "#c00",
                }}
              >
                {errorMsg}
              </div>
            )}

            <form
              onSubmit={handleAddBooking}
              style={{ display: "grid", gap: "15px" }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Client Name *
                </label>
                <input
                  type="text"
                  required
                  value={addBookingData.clientName}
                  onChange={(e) =>
                    setAddBookingData({
                      ...addBookingData,
                      clientName: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Client Email
                  </label>
                  <input
                    type="email"
                    value={addBookingData.clientEmail}
                    onChange={(e) =>
                      setAddBookingData({
                        ...addBookingData,
                        clientEmail: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "#f9fafb",
                      cursor: "not-allowed",
                    }}
                    title="Price is set by the selected service"
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Client Phone
                  </label>
                  <input
                    type="tel"
                    value={addBookingData.clientPhone}
                    onChange={(e) =>
                      setAddBookingData({
                        ...addBookingData,
                        clientPhone: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Select Service *
                </label>
                <select
                  required
                  value={addBookingData.serviceId}
                  onChange={handleServiceChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Choose a service...</option>
                  {services.map((service) => (
                    <option key={service._id} value={service._id}>
                      {service.name} - ${service.price} - {service.duration}{" "}
                      minutes
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={addBookingData.bookingDate}
                    onChange={(e) =>
                      setAddBookingData({
                        ...addBookingData,
                        bookingDate: e.target.value,
                      })
                    }
                    min={(() => {
                      const today = new Date();
                      const year = today.getFullYear();
                      const month = String(today.getMonth() + 1).padStart(
                        2,
                        "0",
                      );
                      const day = String(today.getDate()).padStart(2, "0");
                      return `${year}-${month}-${day}`;
                    })()}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={addBookingData.bookingTime}
                    onChange={(e) =>
                      setAddBookingData({
                        ...addBookingData,
                        bookingTime: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    required
                    min="15"
                    step="15"
                    value={addBookingData.duration}
                    readOnly
                    placeholder="Select a service first"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "#f9fafb",
                      cursor: "not-allowed",
                    }}
                    title="Duration is set by the selected service"
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Price ($) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={addBookingData.price}
                    readOnly
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Notes (optional)
                </label>
                <textarea
                  value={addBookingData.notes}
                  onChange={(e) =>
                    setAddBookingData({
                      ...addBookingData,
                      notes: e.target.value,
                    })
                  }
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                  placeholder="Any special instructions or notes..."
                />
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    background: "#fff",
                    color: "#666",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: "12px",
                    border: "none",
                    borderRadius: "6px",
                    background: submitting ? "#ccc" : "#001d4a",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  {submitting ? "Creating..." : "Create Booking"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </FreelancerSidebarLayout>
  );
}

export default FreelancerBookings;
